/**
 * The persona prompt module shape and its factory. Kept apart from routing.ts
 * because routing imports every mode, and every mode calls definePersona at
 * module load — defining it in routing made that an import cycle.
 */

import { sectionText, type PromptSection } from './identity'

/**
 * Every non-default persona prompt module exports a value of this shape.
 * Enforced by the `definePersona` factory below — author each mode file as
 * `export const <name> = definePersona({ ... })` and TypeScript will catch
 * a missing/extra field at compile time.
 *
 * Study Designer is exempt (it has its own bespoke buildSystemPrompt).
 */
export interface PersonaPromptModule {
  /** The persona's <role>...</role> fragment (shared layers are prepended at assembly). */
  ROLE: string
  /** The persona's <interaction_model>...</interaction_model> fragment. */
  INTERACTION: string
  /** The persona's <tool_usage>...</tool_usage> fragment, every gated block included. */
  TOOL_USAGE: string
  /**
   * Composed section list (ROLE, INTERACTION, tool usage, ...extras), filtered
   * by the user's capabilities and joined at request time.
   */
  SECTIONS: readonly PromptSection[]
}

/**
 * Factory enforcing the PersonaPromptModule shape. Each persona file should
 * call this once at the top level. `extras` accepts shared fragments
 * (CDISC_KNOWLEDGE, STRIDE_ACCESS, etc.) appended after the persona's own
 * three sections.
 */
export function definePersona(parts: {
  role: string
  interaction: string
  /** One block, or several when some are gated on a capability. */
  toolUsage: string | readonly PromptSection[]
  extras?: readonly PromptSection[]
}): PersonaPromptModule {
  const toolUsage = typeof parts.toolUsage === 'string' ? [parts.toolUsage] : parts.toolUsage
  return {
    ROLE: parts.role,
    INTERACTION: parts.interaction,
    TOOL_USAGE: toolUsage.map(sectionText).join('\n\n'),
    SECTIONS: [parts.role, parts.interaction, ...toolUsage, ...(parts.extras ?? [])],
  }
}
